import SearchGradientIcon from 'ui-components/SearchGradientIcon';
import { useTheme } from '@emotion/react'
import React, { useCallback, useState } from 'react'
import AsyncTableListing from 'ui-components/AsyncTableListing'
import { Box, Grid, IconButton, useMediaQuery, Tooltip } from '@mui/material'
import CustomInputField from 'ui-components/CustomInputField'
import { Search, Visibility as VisibilityIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import ModalComponent from 'ui-components/ModalComponent'
import FilterPopup from './popups/FilterPopup'
import Cookies from 'js-cookie'
import { FetchAllSubscriptions } from 'api/subscription'
import moment from 'moment'
import placeHolderImage from "assets/no_data_images/no_data_feedbacks_views.svg"
import { Primary } from 'ui-components/Buttons';

const useStyles = makeStyles((theme) => ({
  sortButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    userSelect: 'none'
  }
}));

const Subscriptions = ({ tabValue = "subscriptions", setLoading = () => { }, setCount = () => { } }) => {
  const theme = useTheme();
  const classes = useStyles();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const investor_id = Cookies.get("X-INV-ID");

  const paginationCookie = "backrr_inv_subscriptions_pagination";
  const searchCookie = "backrr_inv_subscriptions_search";
  const filtersCookie = "backrr_inv_subscriptions_filters";
  const sortCookie = "backrr_inv_subscriptions_sort";

  const [search, setSearch] = React.useState(Cookies.get(searchCookie) || '');
  const [searchStatus, setSearchStatus] = React.useState(!!Cookies.get(searchCookie));
  const [reset, setReset] = React.useState(false);
  const [page, setPage] = React.useState(Number(Cookies.get(paginationCookie)) || 1);
  const [limit, setLimit] = React.useState(10);
  const [queryParams, setQueryParams] = React.useState(() => {
    const saved = Cookies.get(filtersCookie);
    return saved ? JSON.parse(saved) : {};
  });
  const [sortConfig, setSortConfig] = React.useState(() => {
    const saved = Cookies.get(sortCookie);
    return saved ? JSON.parse(saved) : { key: null, direction: null };
  });

  const subscriptionsColumns = React.useMemo(() => ([
    { Header: 'Plan Name', accessor: 'plan_type', id: 'plan_type', minWidth: 150 },
    { Header: 'Invoice Date', accessor: 'invoice_date', id: 'invoice_date', minWidth: 150, align: "center", sortable: true },
    { Header: 'Amount Paid', accessor: 'amount_paid', id: 'amount_paid', minWidth: 120, align: "center", sortable: true },
    { Header: 'View Invoice', accessor: 'view_invoice', id: 'actions', minWidth: 120, align: 'center' }
  ]), []);

  const SubscriptionsRowActions = ({ row }) => {
    const openInvoice = () => {
      const url = row?.original?.invoice_pdf_url;
      if (url) window.open(url, '_blank');
    };
    return row?.original?.invoice_pdf_url ? (
      <Tooltip title="View Invoice">
        <IconButton onClick={openInvoice} size={isSmall ? 'small' : 'medium'}>
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    ) : "-";
  };

  const CustomLink = ({ children }) => <Box sx={{ cursor: 'default' }}>{children}</Box>;

  const { data: subscriptions, isLoading: isLoadingSubscriptions, isRefetching: isRefetchingSubscriptions, refetch: refetchSubscriptions } =
    FetchAllSubscriptions({ page, limit, search: searchStatus ? search : "", queryParams, investor_id });

  const refetchListing = useCallback(() => { refetchSubscriptions(); }, [refetchSubscriptions]);

  React.useEffect(() => { refetchListing(); }, [reset]);
  React.useEffect(() => { setCount(subscriptions?.pagination?.total); }, [subscriptions?.pagination?.total]);

  const sortData = (data, key, direction) => {
    if (!key || !direction) return data;
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === 'amount_paid') {
        aVal = parseFloat(String(a[key]).replace(/[^0-9.-]+/g, "")) || 0;
        bVal = parseFloat(String(b[key]).replace(/[^0-9.-]+/g, "")) || 0;
      }
      return direction === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    Cookies.set(sortCookie, JSON.stringify(newSortConfig));
  };

  const subscriptionsData = React.useMemo(() => {
    const rawData = subscriptions?.data?.map((row) => ({
      ...row,
      invoice_date: row?.invoice_date ? moment(row?.invoice_date)?.format("DD-MMM-YYYY") : "-",
      plan_type: row?.line_items?.map(item => item?.name)?.[0]?.replaceAll("Subscription", "") || "-",
      amount_paid: row?.amount_paid && row?.zoho_invoice_snapshot?.currency_symbol ? row?.zoho_invoice_snapshot?.currency_symbol + " " + row?.amount_paid : "-",
    })) || [];
    return sortData(rawData, sortConfig.key, sortConfig.direction);
  }, [subscriptions?.data, sortConfig]);

  const [filterToggle, setFilterToggle] = useState(false);

  const handleFilterSubmit = ({ query }) => {
    setQueryParams(query);
    Cookies.set(filtersCookie, JSON.stringify(query));
    setReset(!reset);
    setPage(1);
    setFilterToggle(false);
  };

  const handleFilterReset = () => {
    setQueryParams({});
    Cookies.remove(filtersCookie);
    setSortConfig({ key: null, direction: null });
    Cookies.remove(sortCookie);
    setReset(!reset);
    setPage(1);
    setFilterToggle(false);
  };

  const CustomColumnHeader = ({ column }) => {
    if (!column.sortable) return <>{column.Header}</>;
    const isActive = sortConfig.key === column.id;
    return (
      <Box className={classes.sortButton} onClick={() => handleSort(column.id)} sx={{ justifyContent: column.align === 'center' ? 'center' : 'flex-start', fontWeight: isActive ? 600 : 400 }}>
        {column.Header}
        {isActive && (sortConfig.direction === 'asc'
          ? <ArrowUpward fontSize="small" sx={{ color: theme.palette.customColors.b300 }} />
          : <ArrowDownward fontSize="small" sx={{ color: theme.palette.customColors.b300 }} />
        )}
      </Box>
    );
  };

  return (
    <>
      <ModalComponent open={filterToggle} onClose={() => setFilterToggle(false)} style={{ maxWidth: 700, width: "100%" }}>
        <FilterPopup
          tabValue={tabValue}
          queryParams={queryParams || {}}
          setQueryParams={setQueryParams}
          handleClose={() => setFilterToggle(false)}
          handleSubmit={handleFilterSubmit}
          handleReset={handleFilterReset}
        />
      </ModalComponent>

      <Grid container padding={"0.5rem"} marginTop={2} sx={{ border: `1px solid ${theme.palette.customColors.b50} !important`, borderRadius: '8px 8px 0px 0px', background: theme?.palette.customColors.whiteBG, gap: "0.5rem" }}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={3.5} style={{ display: 'flex', height: 'min-content', alignItems: 'center', gap: "0.5rem", maxWidth: 320 }}>
          <CustomInputField
            autoComplete="off"
            sx={{ marginBottom: 0, "& > .MuiFilledInput-root": { borderColor: `${theme.palette.customColors.b50} !important`, borderRadius: '8px' } }}
            fullWidth
            value={search}
            name={`search_${Math.random().toString(36).substring(7)}`}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                Cookies.set(searchCookie, search);
                refetchListing();
                if (search) setSearchStatus(true);
                setPage(1);
                Cookies.set(paginationCookie, 1);
              }
            }}
            onChange={(e) => { setSearch(e.target.value); setSearchStatus(false); }}
            size="small"
            startAdornment={<SearchGradientIcon />}
            placeholder={'Search by Plan Name'}
          />
          <Primary
            onClick={() => {
              if (searchStatus && (search !== '' || Object.keys(queryParams)?.length > 0 || sortConfig.key)) {
                setSearch(''); Cookies.remove(searchCookie); setQueryParams({}); Cookies.remove(filtersCookie); setSortConfig({ key: null, direction: null }); Cookies.remove(sortCookie); setReset(!reset);
              } else {
                Cookies.set(searchCookie, search); refetchSubscriptions();
              }
              setSearchStatus((old) => !old); setPage(1); Cookies.set(paginationCookie, 1);
            }}
            sx={{
              minWidth: { xs: "90px", md: "90px" }, padding: ".7rem 1.25rem",
              bgcolor: searchStatus && (search !== '' || Object.keys(queryParams)?.length > 0 || sortConfig.key) ? "#e9f5f8" : theme.palette.customColors.b300,
              ":hover": { bgcolor: searchStatus && (search !== '' || Object.keys(queryParams)?.length > 0 || sortConfig.key) ? "#e9f5f8" : theme.palette.customColors.b400 },
              color: searchStatus && (search !== '' || Object.keys(queryParams)?.length > 0 || sortConfig.key) ? theme.palette.customColors.b300 : "white"
            }}
          >
            {(searchStatus && (search !== '' || Object.keys(queryParams)?.length > 0 || sortConfig.key)) ? "Reset" : "Search"}
          </Primary>
        </Grid>
      </Grid>

      {(isLoadingSubscriptions || isRefetchingSubscriptions) ? (
        <Grid item xs={12} height={"100vh"} sx={{ width: 'calc(100% - 2px)' }} paddingInline={"0rem !important"}>
          <Box className='skeleton' minHeight={"100vh"} sx={{ borderRadius: "0px 0px 8px 8px", border: `1px solid ${theme.palette.customColors.b50}` }} marginTop={0} />
        </Grid>
      ) : (
        <AsyncTableListing
          columns={subscriptionsColumns}
          data={subscriptionsData}
          CustomLink={CustomLink}
          CustomColumnHeader={CustomColumnHeader}
          RowActions={({ row }) => <SubscriptionsRowActions row={row} />}
          count={subscriptions?.meta?.total}
          sx={{ borderRadius: "0px 0px 8px 8px", maxHeight: "calc(100dvh - 16rem)", border: `1px solid ${theme.palette.customColors.b50} !important` }}
          maxHeight={"calc(100dvh - 16.5rem)"}
          page={page}
          setPage={setPage}
          pageCookieName={paginationCookie}
          limit={limit}
          setLimit={setLimit}
          refetchListing={refetchListing}
          reset={reset}
          noDataPlaceHolderImage={placeHolderImage}
          noDataTitle='No Subscription Invoices'
          noDataSubTitle=''
        />
      )}
    </>
  );
};

export default Subscriptions;
